import FormInputValidationWithFormatAndRegex from 'components/FormEditReceiver/components/FormInputValidationWithFormatAndRegex';
import FormInputDisplayOnly from 'components/common/FormInputDisplayOnly';
import FormInputValidation from 'components/common/FormInputValidation';
import { DESKTOP_MIN_WIDTH } from 'constants';
import withCheckBsbAccount from 'features/to/HOCs/payment/withCheckBsbAccount';
import withCheckBSBNumber from 'features/to/HOCs/payment/withCheckBSBNumber';
import {
  FORM_ADD_BANK_NAME_FIELD,
  FORM_ADD_PAYTO_ACCOUNT_NUMBER_FIELD,
  FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME,
  FORM_ADD_PAYTO_BSB_FIELD,
  FORM_ADD_PAYTO_BSB_NAME,
  accountBsbConfig,
  accountNumberConfig,
} from 'features/to/const';
import usePaytoStore from 'features/to/hooks/payment/usePaytoStore';
import { useDetectDevice } from 'hooks/useDetectDevice';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const FormInputBSBNumber = withCheckBSBNumber(
  FormInputValidationWithFormatAndRegex,
);
const FormInputBankName = FormInputValidation;
const FormInputAccountNumber = withCheckBsbAccount(
  FormInputValidationWithFormatAndRegex,
);

const FormPaytoAccountDetails = ({
  paytoAccountInformation,
  setPaytoAccountInformation,
  paytoAccountValidation,
  setPaytoAccountValidation,
  bsbNumberMaskStr,
  setBsbNumberMaskStr,
  accountNumberMaskStr,
  setAccountNumberMaskStr,
  onPaytoSystemError,
}) => {
  const [isShowFormInputDetails, setShowFormInputDetails] = useState(false);

  const { isAccountAgreementEditing } = usePaytoStore();
  const { isDesktop } = useDetectDevice();

  // Exact-match on msgNumberInvalid2 only (not the broader truthiness
  // isReadOnly uses below) — msgNumberInvalid (unresolved BSB) must keep
  // showing the field read-only, not hidden.
  const isBsbNotSupported =
    paytoAccountValidation?.[FORM_ADD_PAYTO_BSB_NAME] ===
    FORM_ADD_PAYTO_BSB_FIELD?.msgNumberInvalid2;

  useEffect(() => {
    if (isAccountAgreementEditing) {
      setShowFormInputDetails(true);
    }
  }, [isAccountAgreementEditing]);

  const renderFormInputBSB = () => {
    if (isAccountAgreementEditing)
      return (
        <FormInputDisplayOnly
          name={FORM_ADD_PAYTO_BSB_NAME}
          label="BSB"
          value={paytoAccountInformation?.[FORM_ADD_PAYTO_BSB_NAME]}
          styles={{
            marginBottom: 0,
            width: isDesktop ? '27.5%' : '100%',
          }}
          inputStyles={{
            background: '#ECECF0',
            border: '1px solid #DCE3F1',
          }}
        />
      );
    return (
      <FormInputBSBNumber
        field={FORM_ADD_PAYTO_BSB_FIELD}
        config={accountBsbConfig}
        numberMaskStr={bsbNumberMaskStr}
        setNumberMaskStr={setBsbNumberMaskStr}
        information={paytoAccountInformation}
        setInformation={setPaytoAccountInformation}
        validation={paytoAccountValidation}
        setValidation={setPaytoAccountValidation}
        setShowFormInputDetails={setShowFormInputDetails}
        styles={{
          width: isDesktop ? '27.5%' : '100%',
        }}
        errorStyles={{ width: isDesktop ? '436px' : '100%' }}
        isReadOnly={isAccountAgreementEditing}
        onPaytoSystemError={onPaytoSystemError}
      />
    );
  };
  const renderFormInputBSBAndBankName = () => {
    return (
      <FormInputBSBNumberAndBankNameWrap>
        {renderFormInputBSB()}
        {isShowFormInputDetails && (
          <FormInputBankName
            field={FORM_ADD_BANK_NAME_FIELD}
            information={paytoAccountInformation}
            setInformation={setPaytoAccountInformation}
            validation={paytoAccountValidation}
            setValidation={setPaytoAccountValidation}
            isDisplayOnly
            styles={{
              width: isDesktop ? 'calc(100% - 27.5% - 8px)' : '100%',
              transition: '0.3s',
            }}
            inputStyles={{
              borderRadius: '8px',
            }}
            labelStyles={{ marginBottom: '4px' }}
          />
        )}
      </FormInputBSBNumberAndBankNameWrap>
    );
  };

  const renderFormInputAccountNumber = () => {
    if (isAccountAgreementEditing)
      return (
        <FormInputDisplayOnly
          name={FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME}
          label={FORM_ADD_PAYTO_ACCOUNT_NUMBER_FIELD.label}
          value={paytoAccountInformation?.[FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME]}
          inputStyles={{
            background: '#ECECF0',
            border: '1px solid #DCE3F1',
          }}
        />
      );
    if (isShowFormInputDetails && !isBsbNotSupported)
      return (
        <FormInputAccountNumber
          field={FORM_ADD_PAYTO_ACCOUNT_NUMBER_FIELD}
          config={accountNumberConfig}
          numberMaskStr={accountNumberMaskStr}
          setNumberMaskStr={setAccountNumberMaskStr}
          information={paytoAccountInformation}
          setInformation={setPaytoAccountInformation}
          validation={paytoAccountValidation}
          setValidation={setPaytoAccountValidation}
          isReadOnly={
            isAccountAgreementEditing ||
            paytoAccountValidation?.[FORM_ADD_PAYTO_BSB_NAME]
          }
          errorStyles={{
            position: 'unset',
          }}
          onPaytoSystemError={onPaytoSystemError}
        />
      );
    return null;
  };

  return (
    <FormPaytoAccountDetailsStyled>
      {renderFormInputBSBAndBankName()}
      {renderFormInputAccountNumber()}
    </FormPaytoAccountDetailsStyled>
  );
};

const FormPaytoAccountDetailsStyled = styled.div`
  display: grid;
`;

const FormInputBSBNumberAndBankNameWrap = styled.div`
  display: grid;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    display: flex;
    gap: 8px;
  }
`;

export default FormPaytoAccountDetails;
